import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "w-full" }
const _hoisted_2 = ["data-testid"]
const _hoisted_3 = ["data-testid"]

import { computed, ref, watch } from 'vue'
import CatalogCombobox from './CatalogCombobox.vue'
import type { CatalogOption, CatalogSection } from './catalogTypes'
import { useSectorStore } from '@/pinia-store/useSectorStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { useErrorToast } from '@/composables/useErrorToast'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { catalogDisplayName, isRefArchived } from '@/utils/stationDisplay'
import {
  findSectorByName,
  matchesCatalogSearch,
  normalizeCatalogName,
  pendingSectorSuggestions
} from '@/utils/sectorSpecialty'
import type { Sector } from '@/Network/Types/Responses/SectorResponses'
import type { StationSectorRef } from '@/Network/Types/Responses/StationResponses'


export default /*#__PURE__*/_defineComponent({
  __name: 'SectorCombobox',
  props: {
    modelValue: {},
    healthUnitId: {},
    label: { default: 'Setor' },
    emptyText: { default: '— sem setor' },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    testId: { default: 'sector-combobox' }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit }) {

const props = __props;





const sectorStore = useSectorStore()
const permissionsStore = usePermissionsStore()
const { notifyError } = useErrorToast()
const search = ref('')
const createdMessage = ref<string | null>(null)

const slug = (name: string) =>
  normalizeCatalogName(name)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

const canCreate = computed(
  () =>
    !!props.healthUnitId &&
    permissionsStore.check({
      permission: AuthorizationPermissions.HealthUnitEditing,
      resource: {
        objectType: AuthorizationResourcesAndSubjects.HealthUnit,
        objectId: props.healthUnitId
      }
    })
)

watch(
  () => props.healthUnitId,
  (unitId) => {
    // Trocar de unidade descarta a busca e o aviso de criação da unidade anterior.
    search.value = ''
    createdMessage.value = null
    if (unitId) void sectorStore.fetchByHealthUnit(unitId)
  },
  { immediate: true }
)

const allSectors = computed(() => sectorStore.sectorsOf(props.healthUnitId))
const activeSectors = computed(() => sectorStore.activeSectorsOf(props.healthUnitId))
const displayValue = computed(() => catalogDisplayName(props.modelValue))
const loadError = computed(() =>
  props.healthUnitId ? (sectorStore.loadErrorByUnit[props.healthUnitId] ?? null) : null
)
const exactMatch = computed(() => findSectorByName(search.value, allSectors.value))
const footerNote = computed(() =>
  canCreate.value ? null : '🔒 Criar setor exige permissão de editar a unidade'
)

const sections = computed<CatalogSection[]>(() => {
  const result: CatalogSection[] = []
  if (props.modelValue && props.clearable) {
    result.push({
      key: 'clear',
      title: null,
      options: [
        {
          key: 'clear',
          label: '— Sem setor',
          kind: 'clear',
          testId: `${props.testId}-clear-option`
        }
      ]
    })
  }
  result.push({
    key: 'unit',
    title: 'Setores da unidade',
    options: activeSectors.value
      .filter((sector) => matchesCatalogSearch(sector.name, search.value))
      .map((sector) => ({
        key: `sector:${sector.id}`,
        label: sector.name,
        kind: 'option',
        testId: `${props.testId}-option-${sector.id}`
      }))
  })
  if (!canCreate.value) return result
  const suggested = pendingSectorSuggestions(sectorStore.suggestions, allSectors.value).filter(
    (name) => matchesCatalogSearch(name, search.value)
  )
  if (suggested.length) {
    result.push({
      key: 'suggested',
      title: 'Sugeridos · ainda não cadastrados',
      options: suggested.map((name) => ({
        key: `suggest:${name}`,
        label: `＋ ${name}`,
        kind: 'action',
        testId: `${props.testId}-suggestion-${slug(name)}`
      }))
    })
  }
  const typed = search.value.trim()
  if (typed && !exactMatch.value) {
    result.push({
      key: 'create',
      title: null,
      options: [
        {
          key: 'create',
          label: `＋ Criar setor “${typed}” nesta unidade`,
          kind: 'action',
          testId: `${props.testId}-create-option`
        }
      ]
    })
  }
  if (exactMatch.value && isRefArchived(exactMatch.value)) {
    result.push({
      key: 'reactivate',
      title: null,
      options: [
        {
          key: `reactivate:${exactMatch.value.id}`,
          label: `Reativar e usar “${exactMatch.value.name}”`,
          kind: 'action',
          testId: `${props.testId}-reactivate-option`
        }
      ]
    })
  }
  return result
})

async function onOpen(): Promise<void> {
  if (props.healthUnitId) await sectorStore.fetchByHealthUnit(props.healthUnitId)
  if (canCreate.value) await sectorStore.fetchSuggestions()
}

function emitValue(sector: Sector | StationSectorRef | null): void {
  search.value = ''
  emit(
    'update:modelValue',
    sector ? { id: sector.id, name: sector.name, archivedAt: sector.archivedAt ?? null } : null
  )
}

async function onSelect(option: CatalogOption): Promise<void> {
  createdMessage.value = null
  const unitId = props.healthUnitId
  if (!unitId) return
  if (option.key === 'clear') return emitValue(null)
  if (option.key.startsWith('sector:')) {
    const sector = activeSectors.value.find((item) => `sector:${item.id}` === option.key)
    if (sector) emitValue(sector)
    return
  }
  try {
    if (option.key.startsWith('reactivate:')) {
      emitValue(await sectorStore.unarchiveSector(unitId, Number(option.key.split(':')[1])))
      return
    }
    const name = option.key.startsWith('suggest:')
      ? option.key.slice('suggest:'.length)
      : search.value.trim()
    const existing = findSectorByName(name, allSectors.value)
    if (existing) {
      // Homônimo arquivado nunca vira duplicata: o caminho é "Reativar e usar".
      if (!isRefArchived(existing)) emitValue(existing)
      return
    }
    const created = await sectorStore.createSector(unitId, name)
    createdMessage.value = `Setor “${created.name}” criado nesta unidade. Ele também aparece em Unidades › Setores.`
    emitValue(created)
  } catch (error) {
    notifyError(error, 'Não foi possível criar o setor')
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(CatalogCombobox, {
      label: _ctx.label,
      "display-value": displayValue.value,
      "empty-text": _ctx.emptyText,
      placeholder: "Buscar setor",
      sections: sections.value,
      search: search.value,
      disabled: _ctx.disabled || !_ctx.healthUnitId,
      clearable: _ctx.clearable,
      "no-results-text": 
        search.value.trim()
          ? `Nenhum setor encontrado para “${search.value.trim()}”.`
          : 'Nenhum setor cadastrado nesta unidade.'
      ,
      "footer-note": footerNote.value,
      "test-id": _ctx.testId,
      "onUpdate:search": _cache[0] || (_cache[0] = ($event: any) => (search.value = $event)),
      onSelect: onSelect,
      onClear: _cache[1] || (_cache[1] = ($event: any) => (emitValue(null))),
      onOpen: onOpen
    }, null, 8, ["label", "display-value", "empty-text", "sections", "search", "disabled", "clearable", "no-results-text", "footer-note", "test-id"]),
    (createdMessage.value)
      ? (_openBlock(), _createElementBlock("p", {
          key: 0,
          class: "mt-1 text-xs text-green-700",
          "data-testid": `${_ctx.testId}-created-message`
        }, _toDisplayString(createdMessage.value), 9, _hoisted_2))
      : _createCommentVNode("", true),
    (loadError.value)
      ? (_openBlock(), _createElementBlock("p", {
          key: 1,
          class: "mt-1 text-xs text-red-600",
          "data-testid": `${_ctx.testId}-load-error`
        }, _toDisplayString(loadError.value), 9, _hoisted_3))
      : _createCommentVNode("", true)
  ]))
}
}

})